import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Users, Video, CreditCard, TrendingUp, Eye, DollarSign,
  Activity, BarChart3
} from 'lucide-react';
import axios from 'axios';
import { useAuth, API } from '../../App';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';
import { toast } from 'sonner';

const StatCard = ({ icon: Icon, label, value, sub, color = 'text-primary' }) => (
  <div className="bg-misel-card border border-white/10 rounded-sm p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center gap-3 mb-3">
      <div className={`p-2 rounded-sm bg-white/5 ${color}`}>
        <Icon size={18} />
      </div>
      <span className="text-gray-400 text-sm">{label}</span>
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
    {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1a1a1a] border border-white/10 rounded-sm p-3 text-xs">
      <p className="text-gray-400 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-white font-medium">
          {p.name}: {typeof p.value === 'number' && p.name.toLowerCase().includes('revenue')
            ? `€${p.value.toFixed(2)}`
            : p.value}
        </p>
      ))}
    </div>
  );
};

const AnalyticsPage = () => {
  const { i18n } = useTranslation();
  const { token } = useAuth();
  const [overview, setOverview] = useState(null);
  const [topVideos, setTopVideos] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [recentTx, setRecentTx] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const el = i18n.language === 'el';

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    setError(null);
    Promise.all([
      axios.get(`${API}/admin/analytics/overview`, { headers }),
      axios.get(`${API}/admin/analytics/top-videos?locale=${i18n.language}`, { headers }),
      axios.get(`${API}/admin/analytics/revenue-chart?days=30`, { headers }),
      axios.get(`${API}/admin/analytics/recent-transactions`, { headers }),
    ]).then(([ov, tv, rv, tx]) => {
      setOverview(ov.data);
      setTopVideos(tv.data);
      setRevenueData(rv.data);
      setRecentTx(tx.data);
    }).catch(e => {
      console.error('Analytics fetch error', e);
      const message = e.response?.status === 404
        ? (el ? 'Τα αναλυτικά στατιστικά δεν είναι ακόμη διαθέσιμα.' : 'Analytics endpoints are not available yet.')
        : (e.response?.data?.detail || (el ? 'Σφάλμα φόρτωσης αναλυτικών.' : 'Failed to load analytics.'));
      setError(message);
      toast.error(message);
    })
      .finally(() => setLoading(false));
  }, [token, i18n.language, el]);

  if (loading) {
    return (
      <div data-testid="analytics-loading">
        <h1 className="font-roboto font-bold text-lg lg:text-2xl text-white mb-8">
          {el ? 'Στατιστικά' : 'Analytics'}
        </h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-misel-card animate-pulse rounded-sm" />)}
        </div>
        <div className="h-64 bg-misel-card animate-pulse rounded-sm" />
      </div>
    );
  }

  return (
    <div data-testid="analytics-page">
      <h1 className="font-roboto font-bold text-lg lg:text-2xl text-white mb-8">
        {el ? 'Στατιστικά & Αναλύσεις' : 'Analytics & Insights'}
      </h1>

      {error && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-200 rounded-sm p-4 mb-6 text-sm">
          {error}
        </div>
      )}

      {/* Overview Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={Users} label={el ? 'Χρήστες' : 'Users'}
          value={overview?.total_users || 0}
          sub={`+${overview?.new_users_7d || 0} ${el ? 'τελ. 7 ημ.' : 'last 7d'}`}
        />
        <StatCard
          icon={CreditCard} label={el ? 'Συνδρομές' : 'Subscriptions'}
          value={overview?.active_subscriptions || 0}
          color="text-green-400"
        />
        <StatCard
          icon={DollarSign} label={el ? 'Έσοδα 30ημ.' : 'Revenue 30d'}
          value={`€${(overview?.revenue_30d || 0).toFixed(2)}`}
          sub={`${overview?.transactions_30d || 0} ${el ? 'συναλλαγές' : 'transactions'}`}
          color="text-yellow-400"
        />
        <StatCard
          icon={Eye} label={el ? 'Τώρα Βλέπουν' : 'Watching Now'}
          value={overview?.active_viewers || 0}
          color="text-blue-400"
        />
      </div>

      {/* Additional stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <StatCard
          icon={Video} label={el ? 'Βίντεο' : 'Videos'}
          value={overview?.total_videos || 0}
        />
        <StatCard
          icon={TrendingUp} label={el ? 'Νέοι 30ημ.' : 'New Users 30d'}
          value={overview?.new_users_30d || 0}
          color="text-emerald-400"
        />
        <StatCard
          icon={Activity} label={el ? 'Συνολικά Έσοδα' : 'Total Revenue'}
          value={`€${(overview?.total_revenue || 0).toFixed(2)}`}
          color="text-amber-400"
        />
      </div>

      {/* Revenue Chart */}
      <div className="bg-misel-card border border-white/10 rounded-sm p-6 mb-8" data-testid="revenue-chart">
        <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
          <BarChart3 size={20} className="text-primary" />
          {el ? 'Ημερήσια Έσοδα (30 ημέρες)' : 'Daily Revenue (30 days)'}
        </h2>
        {revenueData.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={revenueData}>
              <defs>
                <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#b91c1c" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#b91c1c" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 11 }} tickFormatter={v => v.slice(5)} />
              <YAxis tick={{ fill: '#71717a', fontSize: 11 }} tickFormatter={v => `€${v}`} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="revenue" name={el ? 'Έσοδα' : 'Revenue'} stroke="#b91c1c" fill="url(#revenueGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-64 flex items-center justify-center text-gray-500 text-sm">
            {el ? 'Δεν υπάρχουν δεδομένα εσόδων' : 'No revenue data yet'}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Top Videos */}
        <div className="bg-misel-card border border-white/10 rounded-sm p-6" data-testid="top-videos">
          <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
            <Video size={20} className="text-primary" />
            {el ? 'Δημοφιλή Βίντεο' : 'Top Videos'}
          </h2>
          {topVideos.length > 0 ? (
            <div className="space-y-3">
              {topVideos.slice(0, 10).map((v, i) => (
                <div key={v.video_id} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
                  <span className="text-gray-500 text-sm font-mono w-6">{i + 1}.</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm truncate">{v.title || v.slug}</p>
                  </div>
                  <span className="text-primary text-sm font-medium">{v.views} {el ? 'προβολές' : 'views'}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">{el ? 'Δεν υπάρχουν δεδομένα' : 'No data yet'}</p>
          )}
        </div>

        {/* Recent Transactions */}
        <div className="bg-misel-card border border-white/10 rounded-sm p-6" data-testid="recent-transactions">
          <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
            <CreditCard size={20} className="text-primary" />
            {el ? 'Πρόσφατες Συναλλαγές' : 'Recent Transactions'}
          </h2>
          {recentTx.length > 0 ? (
            <div className="space-y-3">
              {recentTx.slice(0, 10).map((tx) => (
                <div key={tx.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                  <div className="min-w-0">
                    <p className="text-white text-sm truncate">{tx.user_name || tx.user_email}</p>
                    <p className="text-gray-500 text-xs">{tx.payment_type} &middot; {tx.completed_at?.slice(0, 10)}</p>
                  </div>
                  <span className="text-green-400 text-sm font-medium whitespace-nowrap ml-3">
                    €{(tx.amount || 0).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">{el ? 'Δεν υπάρχουν συναλλαγές' : 'No transactions yet'}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
